'use strict'

// Named filter buttons on a multi-record view. The live page shows the
// button name. The rules stay on the server and are applied when the
// request names those button ids.

const BUTTON_ID = /^[A-Za-z_][A-Za-z0-9_]*$/

const MAX_LABEL = 80

const FILTER_BUTTON_VIEW_TYPES = ['table', 'cards', 'maps', 'calendar', 'chart', 'pivot', 'custom']

function buttonIdFromLabel (label, used) {

  const taken = used instanceof Set ? used : new Set(used || [])
  let base = String(label || 'filter').toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '')

  if (!base || !/^[a-z_]/.test(base)) base = base ? `f_${base}` : 'filter'

  base = base.slice(0, 40)

  let id = base
  let n = 2

  while (taken.has(id)) {

    id = `${base}_${n++}`.slice(0, 60)

    if (n > 100) return ''
  }

  return BUTTON_ID.test(id) ? id : ''
}

function allowsMultipleFilterButtons (component) {

  if (!component) return false

  return component.allowMultipleFilterButtons === true || component.allowMultipleFilterButtons === 'true'
}

function normalizeFilterButtons (raw) {

  if (!Array.isArray(raw)) return []

  const used = new Set()
  const buttons = []

  for (const item of raw) {

    if (!item || typeof item !== 'object') continue

    const label = typeof item.label === 'string' ? item.label.trim().slice(0, MAX_LABEL) : ''
    let id = typeof item.id === 'string' ? item.id.trim() : ''

    if (!BUTTON_ID.test(id) || used.has(id)) id = buttonIdFromLabel(label || 'filter', used)

    if (!label || !BUTTON_ID.test(id) || used.has(id)) continue

    used.add(id)

    const filters = Array.isArray(item.filters) ? item.filters.filter((filter) => filter && typeof filter === 'object') : []

    buttons.push({
      id,
      label,
      filters,
      matchMode: item.matchMode === 'any' ? 'any' : 'all'
    })
  }

  return buttons
}

function publicFilterButtons (component) {

  return normalizeFilterButtons(component && component.filterButtons).map((button) => ({
    id: button.id,
    label: button.label
  }))
}

function parsePresetFilterIds (raw) {

  if (Array.isArray(raw)) return raw.map((id) => String(id).trim()).filter(Boolean)

  if (raw === undefined || raw === null || raw === '') return []

  return String(raw).split(',').map((id) => id.trim()).filter(Boolean)
}

function selectedPresetFilterIds (component, raw) {

  const known = new Set(publicFilterButtons(component).map((button) => button.id))
  const ids = []

  for (const id of parsePresetFilterIds(raw)) {

    if (!known.has(id) || ids.includes(id)) continue

    ids.push(id)

    if (!allowsMultipleFilterButtons(component)) break
  }

  return ids
}

function presetFilterGroups (component, raw) {

  const selected = new Set(selectedPresetFilterIds(component, raw))

  return normalizeFilterButtons(component && component.filterButtons)
    .filter((button) => selected.has(button.id) && button.filters.length)
    .map((button) => ({
      filters: button.filters,
      matchMode: button.matchMode
    }))
}

function togglePresetFilterIds (current, id, allowMultiple) {

  const active = Array.isArray(current) ? current.filter((item) => item && item !== id) : []
  const wasOn = Array.isArray(current) && current.includes(id)

  if (wasOn) return active

  if (!allowMultiple) return [id]

  return active.concat(id)
}

function presetFilterParam (uniqueTitle) {

  const id = String(uniqueTitle || '').replace(/[^A-Za-z0-9_]/g, '')

  return id ? `pf_${id}` : 'pf'
}

function walkComponents (node, visit) {

  if (!node || typeof node !== 'object') return

  if (Array.isArray(node)) {

    node.forEach((child) => walkComponents(child, visit))

    return
  }

  visit(node)

  if (Array.isArray(node.components)) walkComponents(node.components, visit)
}

function pageHasFilterButtons (page) {

  let found = false

  walkComponents(page, (node) => {

    if (Array.isArray(node.filterButtons) && node.filterButtons.length) found = true
  })

  return found
}

function livePageWithoutFilterRules (page) {

  if (!page || !pageHasFilterButtons(page)) return page

  const copy = JSON.parse(JSON.stringify(page))

  walkComponents(copy, (node) => {

    if (!Array.isArray(node.filterButtons)) return

    node.allowMultipleFilterButtons = allowsMultipleFilterButtons(node)
    node.filterButtons = publicFilterButtons(node)
  })

  return copy
}

module.exports = {
  FILTER_BUTTON_VIEW_TYPES,
  MAX_LABEL,
  allowsMultipleFilterButtons,
  buttonIdFromLabel,
  livePageWithoutFilterRules,
  normalizeFilterButtons,
  pageHasFilterButtons,
  parsePresetFilterIds,
  presetFilterGroups,
  presetFilterParam,
  publicFilterButtons,
  selectedPresetFilterIds,
  togglePresetFilterIds
}
